import PropTypes from 'prop-types';
import React from 'react';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import Accordion from '../../components/Accordion/Accordion';
import CTACard from '../../components/CTACard/CTACard';
import Error from '../../components/Error/Error';

const accordionItems = [
  {
    title: 'What is escrow?',
    answer:
      'Escrow is a separate amount collected along with your scheduled mortgage payment. The funds are collected to pay your homeowners insurance premiums and/or property taxes when they become due. The funds deposited in the escrow account may not be used for any other purpose.',
    slug: '/resources/blog/understanding-escrow/',
    showReadMore: true,
  },
  {
    title: 'What are the benefits of having an escrow account?',
    answer:
      'An escrow account ensures that the homeowner has enough money to pay property taxes and homeowners insurance when they become due. Holding these funds in escrow keeps the homeowner from having to separately save enough money to pay these bills as the lender pays them directly on the borrower’s behalf.  Having an escrow account helps ensure that insurance premiums and property taxes are paid on time.',
    showReadMore: false,
  },
  {
    title: 'What is an escrow agreement?',
    answer:
      'An escrow agreement is an agreement between the lender and the borrower to collect funds to pay insurance and/or taxes. This agreement is signed at loan closing and is binding for the life of the loan.',
    showReadMore: false,
  },
  {
    title: 'Is an escrow account required?',
    answer:
      'Yes. Vanderbilt assumes responsibility for maintaining your insurance and/or taxes. Because the escrow agreement is valid for the life of the loan, the escrow payment may not be discontinued.',
    showReadMore: false,
  },
  {
    title: 'Why do I need homeowner’s insurance?',
    answer:
      'Insurance coverage is required to protect you, as well as the lender, against physical damage and/or loss of value to the home in the event of a covered loss.',
    showReadMore: false,
  },
  {
    title: 'How is my initial escrow payment calculated?',
    answer:
      'Your initial tax escrow payment is determined at the time you apply for financing of your home and land if land is included with your mortgage. The sales price of the home and value of real estate that are included as collateral for your mortgage are multiplied by the most recent rate charged by your taxing authority and becomes your annual property tax estimate. Your initial escrow payment that is collected along with your scheduled mortgage payment is 1/12th of your annual property tax estimate. Future annual tax escrow projections will be determined by taxes owed and paid the previous year.',
    showReadMore: false,
  },
  {
    title: 'What is Escrow Account Analysis?',
    answer:
      'This is an annual review of your escrow account performed by Vanderbilt to determine if your current escrow payment is sufficient to pay your insurance premium(s) and/or taxes. The Escrow Analysis is conducted approximately 45 days prior to the anniversary date of your loan. Adjustment(s) to your escrow payment will be determined at this time.',
    showReadMore: false,
  },
  {
    title: 'What is an Escrow Account Disclosure Statement?',
    answer:
      'This is an annual report distributed in accordance with the requirements of the Real Estate Settlement Procedures Act (RESPA). The Disclosure Statement will provide you with a detailed summary of your escrow account.',
    showReadMore: false,
  },
  {
    title: 'Could my escrow payment change more than once annually?',
    answer:
      'Yes. Because your escrow payment is determined, in part, by the amount of your insurance policy premium, the escrow payment may adjust if you make any changes to your insurance coverage and/or insurance company. An increase or decrease in your insurance premium may require an adjustment to your escrow payment before your annual escrow analysis occurs.',
    showReadMore: false,
  },
  {
    title: 'What is an Advance?',
    answer:
      'An “advance” is a separate amount that is collected along with your scheduled mortgage payment for any expenditure that Vanderbilt paid on your behalf such as delinquent property taxes, lender placed insurance policies, etc. This is permitted by the terms of your mortgage and/or loan note.',
    showReadMore: false,
  },
  {
    title:
      'Can the monthly amount collected consist of both an escrow payment and an advance payment?',
    answer:
      'Yes. For example, you may have a total monthly payment consisting of  both funds placed in escrow to pay your homeowners insurance premiums and funds to pay back an advance Vanderbilt made on your behalf to pay delinquent property taxes. The payment for insurance would be considered your monthly escrow payment, and the additional payment for delinquent taxes would be considered an advance.',
    showReadMore: false,
  },
  {
    title:
      'What happens to the money in my escrow account when I pay off my loan?',
    answer:
      'Any surplus balance in your escrow account will be refunded to you.',
    showReadMore: false,
  },
  {
    title:
      'Will Vanderbilt contact my insurance company before my insurance policy expires?',
    answer:
      'No. It is the responsibility of our customer(s) to ensure that Vanderbilt receives proof of insurance coverage on the home annually.',
    showReadMore: false,
  },
  {
    title:
      'What should I do when I receive the renewal notice from my insurance company?',
    answer:
      'Review the notice to confirm that Vanderbilt Mortgage is listed as the Mortgagee. Please note that your Vanderbilt loan number must appear on the policy declaration page to ensure both efficient customer service and accurate coverage information. If Vanderbilt Mortgage and Finance, Inc. ISAOA/ATIMA is listed as the Mortgagee, your insurance company will automatically provide a copy of the renewal notice to our office. Should you determine that Vanderbilt is not the established Mortgagee, please contact your insurance company immediately and ask them to list Vanderbilt Mortgage and Finance, Inc. ISAOA/ATIMA as the Mortgagee.',
    showReadMore: false,
  },
];

function Escrow({ error, isLoading }) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  return (
    <Layout isLoading={isLoading}>
      <SEO
        title='Escrow Benefits and Basics'
        description='What is escrow? Learn more about commonly asked questions on escrow including taxes and escrow payments from the experts at Vanderbilt Mortgage and Finance, Inc.'
      />

      <Heading>
        <h1>Understanding Escrow</h1>
      </Heading>

      <Accordion title={false} link={false} items={accordionItems} />

      <CTACard
        note={
          <>
            <a href='tel:1-800-970-7250' className='type-h4'>
              1-800-970-7250
            </a>
            <p className='type-caption'>
              8:30 a.m. to 8:00 p.m. (ET), Monday-Thursday
              <br />
              8:30 a.m. to 5:30 p.m. (ET), Friday
            </p>
          </>
        }
      >
        <h4>
          Please contact us if you have questions about your escrow account or
          advance.
        </h4>
      </CTACard>
    </Layout>
  );
}

Escrow.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  error: PropTypes.object,
};

export default Escrow;
